import { useState, useEffect } from 'react'
import { XIcon, CropIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ImageEditor, type ImageEditorTexts } from './image-editor'
import { DropZone } from './drop-zone'
import { cn } from '@/lib/utils'
import { ExternalLinkIcon } from 'lucide-react'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

/** Whether a URL points to an already-uploaded server resource (not a local blob or data URL) */
function isServerUrl(url: string | undefined): boolean {
  return !!url && (url.startsWith('http://') || url.startsWith('https://'))
}

export function ImageUpload(props: {
  id?: string
  currentUrl: string | undefined
  file: File | undefined
  onChange: (file: File | undefined) => void
  onRemove: () => void
  label: string
  readOnly: boolean
  contain?: boolean
  enableOpenInTab?: boolean
  enableCrop?: boolean
  aspectRatio?: { width: number; height: number }
  size?: 'default' | 'compact'
  children?: React.ReactNode
  noImageText?: string
  openInNewTabText?: string
  cropImageText?: string
  removeImageText?: string
  dropZoneDescription?: string
  imageEditorTexts?: Partial<ImageEditorTexts>
  className?: string
}) {
  const size = props.size ?? 'default'

  // Derived during render so the new image swaps in the same commit as the new file (an effect paints one frame of
  // the old preview first), and held in state rather than a ref — a render-time ref read is a Compiler bail-out.
  // Revocation belongs to the effect cleanup: revoking during render would kill a URL still on screen if React
  // abandons the pass (Suspense, StrictMode).
  const [prevFile, setPrevFile] = useState<File | undefined>(props.file)
  const [preview, setPreview] = useState<string | null>(() => (props.file ? URL.createObjectURL(props.file) : null))
  if (props.file !== prevFile) {
    setPrevFile(props.file)
    setPreview(props.file ? URL.createObjectURL(props.file) : null)
  }
  useEffect(() => {
    if (!preview) return
    return () => URL.revokeObjectURL(preview)
  }, [preview])

  const [editorOpen, setEditorOpen] = useState(false)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [originalFile, setOriginalFile] = useState<File | null>(null)
  const [cropArea, setCropArea] = useState<{ x: number; y: number; width: number; height: number } | null>(null)

  const displayUrl = preview ?? props.currentUrl
  const isCompact = size === 'compact'

  const imgClass = cn(
    props.contain
      ? `${isCompact ? 'max-h-32' : 'max-h-48'} max-w-full min-h-24 min-w-24 object-contain mx-auto block`
      : 'size-full object-cover',
  )

  const handleFiles = (files: File[]) => {
    const f = files[0]
    if (!f?.type.startsWith('image/')) return
    if (props.enableCrop) {
      setOriginalFile(f)
      setCropArea(null)
    }
    props.onChange(f)
  }

  const handleRemove = () => {
    props.onChange(undefined)
    props.onRemove()
    if (props.enableCrop) {
      setOriginalFile(null)
      setCropArea(null)
    }
  }

  return (
    <div className="flex flex-col">
      {props.readOnly && !displayUrl && (
        <p className="text-muted-foreground text-sm">{props.noImageText ?? 'No image uploaded'}</p>
      )}
      <div
        data-visible={Boolean(displayUrl)}
        className="grid grid-rows-[0fr] overflow-hidden opacity-0 transition-[grid-template-rows,opacity] duration-200 ease-out data-[visible=true]:grid-rows-[1fr] data-[visible=true]:opacity-100"
      >
        <div className="overflow-hidden">
          {displayUrl && (
            <div className={isCompact ? undefined : 'pb-3'}>
              <div
                className={cn(
                  'relative overflow-hidden rounded-[inherit]',
                  props.contain ? 'mx-auto w-fit' : 'flex items-center justify-center',
                  isCompact && 'mx-auto',
                  props.className,
                )}
                style={
                  isCompact && props.aspectRatio
                    ? { width: props.aspectRatio.width, height: props.aspectRatio.height }
                    : props.aspectRatio && !props.contain
                      ? { aspectRatio: `${props.aspectRatio.width} / ${props.aspectRatio.height}` }
                      : undefined
                }
              >
                <img key={displayUrl} src={displayUrl} alt="" className={imgClass} />
                {props.readOnly ? (
                  isServerUrl(props.currentUrl) && (
                    <TooltipProvider>
                      <div className="absolute top-1 right-1">
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <a
                                href={props.currentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={props.openInNewTabText ?? 'Open in new tab'}
                                className="inline-flex rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
                              >
                                <ExternalLinkIcon className="size-3.5" />
                              </a>
                            }
                          />
                          <TooltipContent>{props.openInNewTabText ?? 'Open in new tab'}</TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  )
                ) : (
                  <TooltipProvider>
                    <div className="absolute top-1 right-1 flex gap-1">
                      {props.enableOpenInTab && isServerUrl(props.currentUrl) && (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <a
                                href={props.currentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={props.openInNewTabText ?? 'Open in new tab'}
                                className="rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
                              >
                                <ExternalLinkIcon className="size-3.5" />
                              </a>
                            }
                          />
                          <TooltipContent>{props.openInNewTabText ?? 'Open in new tab'}</TooltipContent>
                        </Tooltip>
                      )}
                      {props.enableCrop && (props.file || originalFile) && (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <ButtonPrimitive
                                type="button"
                                aria-label={props.cropImageText ?? 'Crop image'}
                                onClick={() => {
                                  const source = originalFile ?? props.file
                                  if (!source) return
                                  setOriginalFile(source)
                                  setPendingFile(source)
                                  setEditorOpen(true)
                                }}
                                className="rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
                              >
                                <CropIcon className="size-3.5" />
                              </ButtonPrimitive>
                            }
                          />
                          <TooltipContent>{props.cropImageText ?? 'Crop image'}</TooltipContent>
                        </Tooltip>
                      )}
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <ButtonPrimitive
                              type="button"
                              aria-label={props.removeImageText ?? 'Remove image'}
                              onClick={handleRemove}
                              className="rounded-full bg-black/50 p-1 text-white transition-colors hover:bg-black/70"
                            >
                              <XIcon className="size-3.5" />
                            </ButtonPrimitive>
                          }
                        />
                        <TooltipContent>{props.removeImageText ?? 'Remove image'}</TooltipContent>
                      </Tooltip>
                    </div>
                  </TooltipProvider>
                )}
              </div>
            </div>
          )}
          {displayUrl && props.children && <div className="pb-3">{props.children}</div>}
        </div>
      </div>
      <div
        data-visible={!(isCompact && displayUrl)}
        className="grid grid-rows-[0fr] overflow-hidden opacity-0 transition-[grid-template-rows,opacity] duration-200 ease-out data-[visible=true]:grid-rows-[1fr] data-[visible=true]:opacity-100"
      >
        <div className="overflow-hidden">
          <div
            className="grid"
            style={{
              gridTemplateRows: props.readOnly ? '0fr' : '1fr',
              opacity: props.readOnly ? 0 : 1,
              transition: 'grid-template-rows 200ms, opacity 200ms',
            }}
          >
            <div className="-m-1 overflow-hidden p-1">
              <DropZone
                id={props.id}
                label={props.label}
                description={props.dropZoneDescription}
                onFiles={handleFiles}
              />
            </div>
          </div>
        </div>
      </div>
      {props.enableCrop && (
        <ImageEditor
          open={editorOpen}
          file={pendingFile}
          aspectRatio={props.aspectRatio}
          initialCropArea={cropArea || undefined}
          onSave={(croppedFile, newCropArea) => {
            if (!originalFile) setOriginalFile(pendingFile)
            setCropArea(newCropArea)
            props.onChange(croppedFile)
            setEditorOpen(false)
            setPendingFile(null)
          }}
          onOpenChange={(open) => {
            if (!open) {
              setEditorOpen(false)
              if (!props.file) {
                setPendingFile(null)
                setCropArea(null)
                setOriginalFile(null)
              }
            }
          }}
        />
      )}
    </div>
  )
}
